import React, { useState, useEffect } from "react";
import { TextField, Popover, Button, Box, Tooltip } from "@mui/material";
import Checkbox from '@mui/material/Checkbox';
import EventIcon from "@mui/icons-material/Event";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { processDate } from "../../helper/dateHelper";
import { renderAlert } from "../../../../CommonComponents/Alert/Alert";
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import { ignoredKeys, timeSeriesChildColumnListData } from "./constants/MdvConstant";
import AutocompleteFieldForMDV from "./AutoCompleteFieldForMDV";
import PropTypes from "prop-types";
import { validatePropValue } from "../../helper/MdvHelper";
import styles from "./HandleMultiDimensionInputBox.module.css"
const HandleMultiDimensionalInputBox = ({
  item,
  index,
  handlePropValueChange,
  handleTabPress,
  textFieldRefs,
  inputRef,
  timeSeries,
  checkBoxClicked,
  showCheckBox,
  handleKeyPress,
  params,
  data
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(item.hours || '');
  const [minutes, setMinutes] = useState(item.minutes || '');
  const [checked,setChecked] = useState(false)
  const isTimeSeriesDefaultValue = timeSeriesChildColumnListData.some(
    (data) =>
      data.propName === item.propName && data.propAbbrevation === item.propAbbrevation
  )
  
  // Focus input on initial render if index === 0
  useEffect(() => {
    if (index === 0) {
      const mainRef = textFieldRefs.current[`main_${index}`];
      const mainRef1 = textFieldRefs.current[`main_${index}_1`];
  
      if (mainRef) {
        mainRef.focus();
      } else if (mainRef1) {
        mainRef1.focus();
      }
    }
  }, [index, textFieldRefs]);

   
  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  useEffect(() => {
  const convertPropValueToTime = (value) => {
    const numValue = parseFloat(value);
    const hours = Math.floor(numValue);
    const decimalPart = value && typeof value === 'string' ? value.split('.')[1] || '0' : '0';

    let minutes;
      minutes = parseInt(decimalPart)
    if (minutes >= 60) {
      const extraHours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      setHours(hours + extraHours);
      setMinutes(remainingMinutes);
    } else {
      setHours(hours);
      setMinutes(minutes);
    }
  };

  if (item.propValue && item.propType !== 'multi-list' && item.propType !== 'single-list') {
    convertPropValueToTime(item.propValue);
  }
}, [item.propValue]);

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleCalendarDateChange = (newValue) => {
    handlePropValueChange(index, newValue?.format("MM/DD/YYYY"), item.propType);
    handlePopoverClose();
  };

  const handleToday = () => {
    handlePropValueChange(index, dayjs().format("MM/DD/YYYY"), item.propType);
    handlePopoverClose();
  };

  const handleYesterday = () => {
    const yesterday = dayjs().subtract(1, 'day').format('MM/DD/YYYY')
    handlePropValueChange(index, yesterday, item.propType);
    handlePopoverClose()
  }

  const handleTomorrow = () => {
    const tomorrow = dayjs().add(1, 'day').format('MM/DD/YYYY')
    handlePropValueChange(index, tomorrow, item.propType);
    handlePopoverClose()
  }

  const handleClear = () => {
    handlePropValueChange(index, "", item.propType);
    handlePopoverClose();
  };

  const handleTimeChange = (fieldType, value) => {
    const isInteger = value === ''?true:/^[0-9]+$/.test(value);
    const isDecimal = /^\d*\.\d*$/.test(value);

    if(isDecimal){
      renderAlert("Decimal values are not allowed. Please enter integer values.", 'warning');
      return;
    }
    if(!isInteger){
      renderAlert('Please enter valid numbers only', 'warning');
      return;
    }
    if (fieldType === 'hours') {
      if (value > 24) {
        renderAlert("Hours cannot exceed 24. Please enter a value between 0 and 24.", 'error');
        return;
      }
      if (value == 24 && minutes > 0) {
        renderAlert("Total time cannot exceed 24 hours. Set minutes to 0 if hours is 24.", 'error');
        setHours(23)
        return;
      }
      setHours(value);
    } else if (fieldType === 'minutes') {
      if (value > 59) {
        renderAlert("Minutes cannot exceed 59. Please enter minutes between 0 and 59.", 'error');
        return;
      }
      if (value > 0 && hours == 24) {
        setMinutes(0);
        renderAlert("Total time cannot exceed 24 hours.", 'error');
        return
      }
      else {
        setHours(hours);
        setMinutes(value);
      }
     
    };


    const newHours = fieldType === 'hours' ? value : hours;
    const newMinutes = fieldType === 'minutes' ? value : minutes;

    let totalHours = parseFloat(newHours) || 0;
    let totalMinutes = parseFloat(newMinutes) || 0;

    var totalTime = totalHours + (totalMinutes) / 100;
    handlePropValueChange(index, totalTime.toFixed(2), 'number');
  };

  const handleBlur = (event) => {
    let value = event.target.value;
    if (item.propType === "date" && value) {
      const formattedDate = processDate(value);
      value = dayjs(formattedDate).format("MM/DD/YYYY");
      handlePropValueChange(
        index,
        value === "Invalid Date" ? "" : value,
        item.propType
      );
    }
  };

  // Custom tab handling
  const handleKeyDown = (index, event) => {
    if (item.propType === 'number' && timeSeries === "timeseries" && event.key !== "Tab" ) {
      if (event.key === '.') {
        renderAlert("Decimal points are not allowed for this input", "warning", 2000);
      }
      else {
        const isInteger = event.key === '' ? true : /^[0-9]+$/.test(event.key);

        if (ignoredKeys.has(event.key)) {
          return;
        }
        if (!isInteger) {
          renderAlert('Please enter valid numbers only', 'warning');
          return event.key;
        }
      }
    }
    if (event.key === "Tab") {
      event.preventDefault()
      event.stopPropagation()  
      handleTabPress(index, event, item.propType);
    }
  };

  const renderInputField = () => {
    if (item.propType === "date") {
      return (
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <input
          className={styles.dateInputField}
            type="text"
            ref={inputRef} // Use the passed inputRef prop here
            value={item.propValue}
            onChange={(e) =>
              handlePropValueChange(index, e.target.value, item.propType)
            }
            onBlur={handleBlur}
            placeholder="MM/DD/YYYY"
            inputRef={(el) => (textFieldRefs.current[`main_${index}`] = el)}
            onKeyDown={(event) => handleKeyDown(`main_${index}`, event)}
            tabIndex={`main_${index}`}
          />
          <EventIcon
            onClick={handlePopoverOpen}
            sx={{ fontSize: "1rem" }}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            disablePortal={true}
            style={{width: "350px"}}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                onChange={handleCalendarDateChange}
                value={item.propValue !== "" ? dayjs(item.propValue) : null}
                sx={{ height: "16.5rem", marginRight: "0px", marginLeft: "0px" }}
                minDate={dayjs("1600-01-01")}
                maxDate={dayjs("2200-01-01")}
              />
            </LocalizationProvider>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingBottom: "0.75rem",
                paddingLeft: "0.25rem",
                paddingRight: "1.25rem"
              }}
            >
              <Button
                onClick={handleClear}
                sx={{
                  fontSize: "0.75rem",
                  padding: "0",
                  textTransform: "none",
                  fontWeight: "bold",
                }}
              >
                Clear
              </Button>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button onClick={handleYesterday} sx={{ fontSize: "0.75rem", padding: "0", textTransform: "none", fontWeight: "bold" }}>Yesterday</Button>
                <Button onClick={handleToday} sx={{ fontSize: "0.75rem", padding: "0", textTransform: "none", fontWeight: "bold" }}>Today</Button>
                <Button onClick={handleTomorrow} sx={{ fontSize: "0.75rem", padding: "0", textTransform: "none", fontWeight: "bold" }}>Tomorrow</Button>
              </Box>
            </Box>
          </Popover>
        </div>
      );
    } else {
      return (
        <>
          {item.propType === 'number' && timeSeries === "timeseries" && isTimeSeriesDefaultValue ? (
            <Box display="flex" alignItems="center">
              <TextField
                value={hours === 0 ? '0' : hours || ""}
                onChange={(e) => handleTimeChange('hours', e.target.value)}
                onBlur={handleBlur}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  width: !showCheckBox?'100%':'100px',
                  marginRight: '5px',
                  ".MuiOutlinedInput-root": {
                    height: "25px",
                    fontSize: "12px",
                  },
                }}
                inputRef={(el) => (textFieldRefs.current[`main_${index}_1`] = el)}
                onKeyDown={(e) => handleKeyDown(`main_${index}_1`, e)}
                type="number"
                placeholder="Hrs"
                tabIndex={`main_${index}_1`}
              />

              {/* Label for separator */}
              <Box component="span" sx={{ marginRight: '5px', fontSize: '12px' }}>:</Box>

              <TextField
                value={minutes || ''}
                onChange={(e) => handleTimeChange('minutes', e.target.value)}
                onBlur={handleBlur}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  width: !showCheckBox?'100%':'100px',
                  ".MuiOutlinedInput-root": {
                    height: "25px",
                    fontSize: "12px",
                  },
                }}
                inputProps={{
                  min: 0,
                  max: 59, // Set the max limit to 59 
                }}
                inputRef={(el) => (textFieldRefs.current[`main_${index}_2`] = el)}
                onKeyDown={(e) => handleKeyDown(`main_${index}_2`, e, 'number')}
                type="number"
                placeholder="Mins"
                tabIndex={`main_${index}_2`}
              />
              {showCheckBox && <Tooltip title="Copy this value till the task's end Date">
                <Checkbox checked={checked}
                  onChange={(event) => { setChecked(event.target.checked); checkBoxClicked(index, event.target.checked) }}
                  icon={<ContentPasteGoIcon />} // Icon when checkbox is unchecked
                  checkedIcon={<ContentPasteGoIcon />}
                  tabIndex={index * 3 + 2} // Add tabIndex for checkbox
                  inputRef={(el) => (textFieldRefs.current[`main_${index}_3`] = el)} // Ensure ref for checkbox
                  onKeyDown={(e) => handleKeyDown(`main_${index}_3`, e, 'number')}
                />
              </Tooltip>}

            </Box>
          ) : (
            <TextField
              value={item.propValue}
              onChange={(e) => {
                const newValue = e.target.value;
                if (validatePropValue(newValue, item.propType, params?.colDef, item)) {
                  handlePropValueChange(index, newValue, item.propType, item);
                  } else {
                    e.target.value = item.propValue || ""; // Reset UI to last valid value
                  }
                }}
                onBlur={(e) => {
                  handleBlur(e);
                }}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                sx={{
                  maxHeight: "fitContent",
                  overflow: "auto",
                  ".MuiOutlinedInput-root": {
                    height: "auto", 
                    fontSize: "12px",
                    padding: "4px", 
                  },
                  ".MuiOutlinedInput-input": {
                    maxHeight: "fitContent",
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                  },
                }}
              inputRef={(el) => {
                textFieldRefs.current[`main_${index}`] = el;
              }}
              onKeyDown={(event) => {handleKeyDown(`main_${index}`, event)}}
              type={item.propType}
              inputProps={
                item.propType === "number"
                  ? { inputMode: "numeric", pattern: "[0-9]*" }
                  : {}
              }
            />
          )}
        </>

      );
    }
  };

  const renderField = () => {
    if (item.propType === "boolean") {
      return (
        <select
          className="MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-formControl MuiInputBase-sizeSmall"
          name="propType"
          autoFocus={index === 0}
          value={item.propValue}
          style={{
            width: "100%",
            padding: "3px 8.5px",
            border: "0.5px solid #babfc7",
            color: item.propType === "none" ? "rgb(186, 191, 199)" : "black",
            fontSize: "12px",
            height: "25px",
            borderRadius: "4px",
          }}
          onChange={(e) => handlePropValueChange(index, e.target.value)}
          onFocus={(e) => (e.target.style.border = "2px solid rgb(25, 118, 210)")}
          onBlur={(e) =>
            (e.target.style.border = "0.5px solid rgb(186, 191, 199)")
          }
          onMouseOver={(e) => (e.target.style.border = "0.5px solid black")}
          onMouseOut={(e) => {
            if (document.activeElement !== e.target) {
              e.target.style.border = "0.5px solid rgb(186, 191, 199)";
            }
          }}
          ref={(el) => {
            textFieldRefs.current[`main_${index}`] = el;
          }}
          onKeyDown={(event) => {
            handleKeyDown(`main_${index}`, event);
          }}
        >
          <option value="">Select</option>
          <option value="N">N</option>
          <option value="Y">Y</option>
        </select>
      );
    }else if (item.propType === "multi-list" || item.propType === "single-list") {
      return (
        <AutocompleteFieldForMDV
          item={item}
          index={index}
          handlePropValueChange={handlePropValueChange}
          textFieldRefs={textFieldRefs}
          handleKeyDown={handleKeyDown}
        />
      );
    } else return <div>{renderInputField()}</div>;
  };

  return <>{renderField()}</>;
  };

  HandleMultiDimensionalInputBox.propTypes = {
    item: PropTypes.shape({
      propName: PropTypes.string,
      propAbbrevation: PropTypes.string,
      propValue: PropTypes.any,
      propType: PropTypes.string,
      propBgColor: PropTypes.string,
      propFgColor: PropTypes.string,
      propList: PropTypes.any // Adjust if propValue can be another type
    }).isRequired,
    index: PropTypes.number.isRequired,
    handlePropValueChange: PropTypes.func.isRequired,
    textFieldRefs: PropTypes.object.isRequired,
    params: PropTypes.any,
    handleKeyPress: PropTypes.func
  };
  
export default HandleMultiDimensionalInputBox;
